import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Search, X } from 'lucide-react'
import { searchNavigationTarget } from '../../utils/searchNavigation'
import { useRecentSearches } from '../../hooks/useRecentSearches'

const LABEL = 'Søg på CVR-nummer, virksomhed eller person'
const PLACEHOLDER = 'Søg CVR-nummer, virksomhed eller person'

const ICON_BTN =
  'inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-600 hover:bg-rule-soft hover:text-ink transition-colors'

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

interface SearchFieldProps {
  id: string
  query: string
  onQueryChange: (q: string) => void
  onSubmit: (e: React.FormEvent) => void
  inputRef?: React.Ref<HTMLInputElement>
  showShortcut?: boolean
  className?: string
}

const SearchField: React.FC<SearchFieldProps> = ({
  id,
  query,
  onQueryChange,
  onSubmit,
  inputRef,
  showShortcut = false,
  className = '',
}) => (
  <form role="search" onSubmit={onSubmit} className={`relative ${className}`}>
    <label htmlFor={id} className="sr-only">
      {LABEL}
    </label>
    <Search
      className="pointer-events-none absolute left-3 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-ink-400"
      aria-hidden
    />
    <input
      ref={inputRef}
      id={id}
      type="search"
      value={query}
      onChange={(e) => onQueryChange(e.target.value)}
      placeholder={PLACEHOLDER}
      autoComplete="off"
      className="peer h-10 w-full rounded-lg border border-rule-strong bg-canvas pl-[38px] pr-11 text-[14.5px] font-medium text-ink placeholder:text-ink-400 transition-colors hover:border-ink-300 focus:bg-paper"
    />
    {showShortcut && !query && (
      <kbd
        aria-hidden
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-rule-strong bg-paper px-1.5 py-px text-xs font-semibold text-ink-500 peer-focus:hidden"
      >
        /
      </kbd>
    )}
  </form>
)

/**
 * App-bar search. On larger screens the field is always visible and »/«
 * jumps to it from anywhere; on phones a magnifier opens it under the bar.
 */
const HeaderSearch: React.FC = () => {
  const navigate = useNavigate()
  const { remember } = useRecentSearches()
  const [query, setQuery] = useState('')
  const [mobileOpen, setMobileOpen] = useState(false)
  const desktopInputRef = useRef<HTMLInputElement>(null)
  const mobileInputRef = useRef<HTMLInputElement>(null)
  const mobileWrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      if (isTypingTarget(e.target)) return
      const input = desktopInputRef.current
      if (!input || input.offsetParent === null) return
      e.preventDefault()
      input.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    mobileInputRef.current?.focus()
    const onDoc = (e: MouseEvent) => {
      if (!mobileWrapRef.current?.contains(e.target as Node)) setMobileOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [mobileOpen])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const target = searchNavigationTarget(query)
    if (!target) return
    remember(query)
    navigate(target)
    setMobileOpen(false)
    setQuery('')
    desktopInputRef.current?.blur()
  }

  return (
    <>
      <SearchField
        id="header-search"
        query={query}
        onQueryChange={setQuery}
        onSubmit={handleSubmit}
        inputRef={desktopInputRef}
        showShortcut
        className="hidden sm:block flex-1 max-w-[520px] min-w-0"
      />

      <div ref={mobileWrapRef} className="sm:hidden ml-auto">
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          className={ICON_BTN}
          aria-label={mobileOpen ? 'Luk søg' : 'Søg'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? (
            <X className="h-[19px] w-[19px]" aria-hidden />
          ) : (
            <Search className="h-[19px] w-[19px]" aria-hidden />
          )}
        </button>
        {mobileOpen && (
          <div className="absolute inset-x-0 top-full z-40 border-b border-rule bg-paper px-4 py-3 shadow-sm">
            <SearchField
              id="header-search-mobile"
              query={query}
              onQueryChange={setQuery}
              onSubmit={handleSubmit}
              inputRef={mobileInputRef}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default HeaderSearch
